import { DEFAULT_QUERY_LANGUAGE } from "./queryLanguage";
import { parseQuery, tokenizeForIndex } from "./parser";
import { rankCandidates } from "./ranker";
import { rvTypeMatches } from "./rvTypes";
import type {
  ParsedQuery,
  QueryLanguageConfig,
  SearchDoc,
  SearchHit,
  SearchIndexFile,
  SearchIndexManifest,
  SearchSuggestion,
  TokenIndexFile,
} from "./types";

export const SEARCH_STORAGE_PREFIX = "search";
export const SEARCH_MANIFEST_KEY = `${SEARCH_STORAGE_PREFIX}/manifest.json`;

function storageBase(): string {
  const url =
    (typeof import.meta !== "undefined" ? import.meta.env?.['VITE_SUPABASE_URL'] : undefined) ??
    (typeof process !== "undefined" ? process.env?.['SUPABASE_URL'] : undefined) ??
    "";
  return `${url}/storage/v1/object/public/splash-assets`;
}

interface LoadedIndex {
  manifest: SearchIndexManifest;
  docs: Map<number, SearchDoc>;
  tokens: Record<string, number[]>;
  vocabulary: string[];
  language: QueryLanguageConfig;
}

let cache: LoadedIndex | null = null;
let inflight: Promise<LoadedIndex> | null = null;
/** When the in-memory copy was downloaded (ms epoch). */
let loadedAt = 0;
/** Re-check the published manifest after this long. */
const REVALIDATE_AFTER_MS = 5 * 60 * 1000;
let revalidating: Promise<void> | null = null;
let visibilityHooked = false;

async function fetchJson<T>(key: string, bust = false): Promise<T> {
  const url = `${storageBase()}/${key}${bust ? `?t=${Date.now()}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load ${key} (${res.status})`);
  return (await res.json()) as T;
}

async function load(): Promise<LoadedIndex> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const manifest = await fetchJson<SearchIndexManifest>(SEARCH_MANIFEST_KEY, true);
    const [index, tokenIndex, language] = await Promise.all([
      fetchJson<SearchIndexFile>(manifest.files.index),
      fetchJson<TokenIndexFile>(manifest.files.tokens),
      fetchJson<QueryLanguageConfig>(manifest.files.language).catch(() => DEFAULT_QUERY_LANGUAGE),
    ]);
    const docs = new Map<number, SearchDoc>();
    for (const d of index.documents) docs.set(d.id, d);
    const loaded: LoadedIndex = {
      manifest,
      docs,
      tokens: tokenIndex.tokens,
      vocabulary: tokenIndex.vocabulary,
      language: { ...DEFAULT_QUERY_LANGUAGE, ...language },
    };
    cache = loaded;
    loadedAt = Date.now();
    return loaded;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/**
 * Cheaply check whether a newer index has been published and swap it in.
 * Only the small manifest is refetched; the big files are re-downloaded solely
 * when the published version actually changed. A long-lived tab therefore stops
 * serving yesterday's inventory after the nightly rebuild.
 */
async function revalidate(force = false): Promise<void> {
  if (!cache) return;
  if (!force && Date.now() - loadedAt < REVALIDATE_AFTER_MS) return;
  if (revalidating) return revalidating;
  revalidating = (async () => {
    try {
      const manifest = await fetchJson<SearchIndexManifest>(SEARCH_MANIFEST_KEY, true);
      const current = cache?.manifest;
      loadedAt = Date.now();
      if (
        current &&
        manifest.generatedAt === current.generatedAt &&
        manifest.files.index === current.files.index
      ) {
        return;
      }
      cache = null;
      await load();
    } catch {
      /* keep serving the copy we already have */
    } finally {
      revalidating = null;
    }
  })();
  return revalidating;
}

/** Re-check on tab focus so a tab left open overnight refreshes itself. */
function hookVisibility(): void {
  if (visibilityHooked || typeof document === "undefined") return;
  visibilityHooked = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void revalidate();
  });
}

function prefixMatches(vocabulary: string[], prefix: string, limit = 12): string[] {
  if (!prefix) return [];
  // binary search on the sorted vocabulary — never scans documents
  let lo = 0;
  let hi = vocabulary.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((vocabulary[mid] ?? "") < prefix) lo = mid + 1;
    else hi = mid;
  }
  const out: string[] = [];
  for (let i = lo; i < vocabulary.length && out.length < limit; i++) {
    const word = vocabulary[i] ?? "";
    if (!word.startsWith(prefix)) break;
    out.push(word);
  }
  return out;
}

function candidateIds(loaded: LoadedIndex, parsed: ParsedQuery): number[] {
  const postings: number[][] = [];
  for (const term of parsed.searchTerms) {
    const direct = loaded.tokens[term];
    if (direct?.length) {
      postings.push(direct);
      continue;
    }
    // prefix expansion for partial words (autocomplete-style typing)
    const expanded = prefixMatches(loaded.vocabulary, term, 8)
      .flatMap((tok) => loaded.tokens[tok] ?? []);
    if (expanded.length) postings.push([...new Set(expanded)]);
  }
  for (const term of parsed.expandedTerms) {
    const direct = loaded.tokens[term];
    if (direct?.length) postings.push(direct);
  }
  if (postings.length === 0) return [];

  // intersection first (token-AND); fall back to union when it is empty
  const sorted = [...postings].sort((a, b) => a.length - b.length);
  let intersection = new Set(sorted[0]);
  for (let i = 1; i < sorted.length && intersection.size; i++) {
    const next = new Set(sorted[i]);
    intersection = new Set([...intersection].filter((id) => next.has(id)));
  }
  if (intersection.size > 0) return [...intersection];
  const union = new Set<number>();
  for (const p of postings) for (const id of p) union.add(id);
  return [...union];
}

export interface SearchResult {
  parsed: ParsedQuery;
  hits: SearchHit[];
  candidateCount: number;
  totalDocuments: number;
  usedFallbackScan: boolean;
  generatedAt: string;
}

function classifyDoc(d: SearchDoc): "inventory" | "guide" | "blog" | "page" {
  if (d.source === "inventory") return "inventory";
  if (d.type === "guide") return "guide";
  if (d.type === "blog") return "blog";
  return "page";
}

/**
 * Count the documents that would actually be RETURNED for a parsed query.
 * This runs the same filter + scoring pipeline as search(), so the counts shown
 * next to a suggestion always agree with the facet counts of its result set.
 */
function countHits(loaded: LoadedIndex, parsed: ParsedQuery) {
  const counts = { inventory: 0, guide: 0, blog: 0, page: 0, total: 0 };
  let ids = candidateIds(loaded, parsed);
  if (
    parsed.weightFilter ||
    parsed.lengthFilter ||
    parsed.priceFilter ||
    parsed.conditionFilter ||
    parsed.rvTypeFilter ||
    parsed.featureTags.length
  ) {
    const matchable = [...loaded.docs.values()]
      .filter(
        (d) =>
          (parsed.weightFilter ? (d.dryWeightLbs ?? 0) > 0 : true) &&
          (parsed.lengthFilter ? (d.lengthFt ?? 0) > 0 : true) &&
          (parsed.priceFilter ? (d.price ?? 0) > 0 : true) &&
          (parsed.conditionFilter ? !!d.condition : true) &&
          (parsed.rvTypeFilter
            ? d.source === "inventory" && rvTypeMatches(parsed.rvTypeFilter.id, d.rvType, d.category)
            : true) &&
          (parsed.featureTags.length
            ? parsed.featureTags.every((t) => (d.featureTags ?? []).includes(t))
            : true),
      )
      .map((d) => d.id);
    ids = [...new Set([...ids, ...matchable])];
  }
  const candidates = ids
    .map((id) => loaded.docs.get(id))
    .filter((d): d is SearchDoc => !!d);
  const hits = rankCandidates(candidates, parsed, loaded.language, Number.MAX_SAFE_INTEGER);
  for (const h of hits) {
    counts[classifyDoc(h.doc)] += 1;
    counts.total += 1;
  }
  return counts;
}

export const SmartSearch = {
  /** Warm the in-memory cache (safe to call repeatedly). */
  async preload(): Promise<SearchIndexManifest> {
    const loaded = await load();
    hookVisibility();
    return loaded.manifest;
  },

  /** Drop the in-memory cache so the next call refetches the published index. */
  invalidate(): void {
    cache = null;
    loadedAt = 0;
  },

  /** Swap in a newer published index if one exists (no-op when unchanged). */
  async refresh(): Promise<void> {
    await revalidate(true);
  },

  async status(): Promise<SearchIndexManifest> {
    await revalidate(true);
    return (await load()).manifest;
  },

  async config(): Promise<QueryLanguageConfig> {
    return (await load()).language;
  },

  async parseQuery(query: string): Promise<ParsedQuery> {
    const loaded = await load().catch(() => null);
    return parseQuery(query, loaded?.language ?? DEFAULT_QUERY_LANGUAGE);
  },

  /** Synchronous parse against the built-in defaults (tests, SSR-safe). */
  parseQuerySync(query: string, cfg: QueryLanguageConfig = DEFAULT_QUERY_LANGUAGE): ParsedQuery {
    return parseQuery(query, cfg);
  },

  async autocomplete(query: string, limit = 10): Promise<string[]> {
    const loaded = await load();
    const raw = query.trim().toLowerCase().split(/\s+/).pop() ?? "";
    if (raw.length < 2) return [];
    const matches = prefixMatches(loaded.vocabulary, raw, limit * 3);
    return matches
      .sort((a, b) => (loaded.tokens[b]?.length ?? 0) - (loaded.tokens[a]?.length ?? 0))
      .slice(0, limit);
  },

  async suggestWithCounts(query: string, limit = 8): Promise<SearchSuggestion[]> {
    const loaded = await load();
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const raw = words.pop() ?? "";
    // Only complete a genuine alphabetic partial word. Numbers and things like
    // "30k" are query operands, not prefixes — completing them produces noise.
    if (raw.length < 2 || !/^[a-z][a-z'-]*$/.test(raw)) return [];
    const prefixMatchesList = prefixMatches(loaded.vocabulary, raw, limit * 4);
    const terms = prefixMatchesList
      .sort((a, b) => (loaded.tokens[b]?.length ?? 0) - (loaded.tokens[a]?.length ?? 0))
      .slice(0, limit * 2);

    const base = words.join(" ");
    const out: SearchSuggestion[] = [];
    const seen = new Map<string, SearchSuggestion>();
    for (const term of terms) {
      const query_ = base ? `${base} ${term}` : term;
      const counts = countHits(loaded, parseQuery(query_, loaded.language));
      if (counts.total === 0) continue; // never suggest a dead end
      // Collapse completions that resolve to the exact same result set
      // ("professional"/"program"/"protect" all scoring the same 6 units is
      // noise) and keep the shortest, most natural word.
      const signature = `${counts.inventory}|${counts.guide}|${counts.blog}|${counts.page}`;
      const existing = seen.get(signature);
      if (existing) {
        if (term.length < existing.term.length) {
          existing.term = term;
          existing.query = query_;
        }
        continue;
      }
      const suggestion: SearchSuggestion = { term, query: query_, base, counts };
      seen.set(signature, suggestion);
      out.push(suggestion);
      if (seen.size >= limit) break;
    }
    return out;
  },

  async search(query: string, options?: { limit?: number; source?: SearchDoc["source"] }): Promise<SearchResult> {
    await load();
    hookVisibility();
    // A tab kept open past the nightly rebuild must not keep serving the old copy.
    await revalidate();
    const loaded = await load();
    const cfg = loaded.language;
    const parsed = parseQuery(query, cfg);
    const limit = options?.limit ?? cfg.maxResults;

    if (query.trim().length < cfg.minQueryLength) {
      return {
        parsed,
        hits: [],
        candidateCount: 0,
        totalDocuments: loaded.docs.size,
        usedFallbackScan: false,
        generatedAt: loaded.manifest.generatedAt,
      };
    }

    let ids = candidateIds(loaded, parsed);
    let usedFallbackScan = false;
    // A numeric filter ("under 5000 lbs") carries no searchable token, so the
    // inventory pool has to be considered even when term matching is thin.
    if (
      parsed.weightFilter ||
      parsed.lengthFilter ||
      parsed.priceFilter ||
      parsed.conditionFilter ||
      parsed.rvTypeFilter ||
      parsed.featureTags.length
    ) {
      const matchable = [...loaded.docs.values()]
        .filter(
          (d) =>
            (parsed.weightFilter ? (d.dryWeightLbs ?? 0) > 0 : true) &&
            (parsed.lengthFilter ? (d.lengthFt ?? 0) > 0 : true) &&
            (parsed.priceFilter ? (d.price ?? 0) > 0 : true) &&
            (parsed.conditionFilter ? !!d.condition : true) &&
            (parsed.rvTypeFilter
              ? d.source === "inventory" && rvTypeMatches(parsed.rvTypeFilter.id, d.rvType, d.category)
              : true) &&
            (parsed.featureTags.length
              ? parsed.featureTags.every((t) => (d.featureTags ?? []).includes(t))
              : true),
        )
        .map((d) => d.id);
      ids = [...new Set([...ids, ...matchable])];
    }
    if (ids.length === 0 && loaded.docs.size <= 5000) {
      // small, bounded fallback only — never the default path
      ids = [...loaded.docs.keys()];
      usedFallbackScan = true;
    }

    let candidates = ids.map((id) => loaded.docs.get(id)).filter((d): d is SearchDoc => !!d);
    if (options?.source) candidates = candidates.filter((d) => d.source === options.source);

    return {
      parsed,
      hits: rankCandidates(candidates, parsed, cfg, limit),
      candidateCount: candidates.length,
      totalDocuments: loaded.docs.size,
      usedFallbackScan,
      generatedAt: loaded.manifest.generatedAt,
    };
  },
};

export { parseQuery, tokenizeForIndex, DEFAULT_QUERY_LANGUAGE };
export type { ParsedQuery, SearchDoc, SearchHit, SearchIndexManifest, QueryLanguageConfig, SearchSuggestion };
